import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConnectionDetail } from './connection-detail';
import { Separator } from '@/components/ui/separator';
import { ConnectionDetailProps } from '@/lib/types';

interface IntegrationCardProps {
  title: string;
  description: ReactNode;
  logo: string;
  isEnabled: boolean;
  connectData: ConnectionDetailProps | undefined;
  onToggle: (checked: boolean) => void;
  comingSoon?: boolean;
  /** Extra controls beside the Manage/Connect button (Refresh, Disconnect). */
  actions?: ReactNode;
  /** Replaces the Manage/Connect label, for an in-flight state ("Connecting..."). */
  actionLabel?: string;
  actionDisabled?: boolean;
}

export const IntegrationCard = ({
  title,
  description,
  logo,
  isEnabled,
  connectData,
  onToggle,
  comingSoon,
  actions,
  actionLabel,
  actionDisabled,
}: IntegrationCardProps) => {
  return (
    <Card className={`rounded-lg border bg-white ${comingSoon ? 'opacity-60' : ''}`}>
      <CardContent className='flex flex-col space-y-3 px-4 py-4'>
        {/* Row 1: Icon, Title, Connect Button */}
        <div className='flex justify-between items-center w-full'>
          <div className='flex items-center space-x-3'>
            <div className='w-10 h-10 relative flex-shrink-0'>
              <img src={logo} alt={`${title} logo`} className='size-full object-contain' />
            </div>
            <h3 className='text-base font-medium text-gray-900'>{title}</h3>
            {comingSoon && (
              <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600'>
                Coming Soon
              </span>
            )}
          </div>
          {!comingSoon && (
            <div className='flex items-center gap-2 shrink-0'>
              <Button
                size="sm"
                variant="outline"
                className="text-gray-900"
                onClick={() => onToggle(!isEnabled)}
                disabled={actionDisabled}
              >
                {actionLabel ?? (isEnabled ? 'Manage' : 'Connect')}
              </Button>
              {actions}
            </div>
          )}
        </div>
        {/* Row 2: Description */}
        <p className='text-sm text-gray-500'>{description}</p>
        {connectData && connectData !== undefined && (
          <>
            <Separator />
            <ConnectionDetail
              connectedDate={connectData.connectedDate}
              accountId={connectData.accountId}
              apiKey={connectData.apiKey}
              serviceName={connectData.serviceName}
              accountIdLabel={connectData.accountIdLabel}
              accountNameLabel={connectData.accountNameLabel}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
